import _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../../commonInfra/featureFlags/FeatureFlags';
import { imgPath } from '../../commonInfra/utils/ImgUrl';
import { AuthorizableType } from './AuthorizableType';

export enum Capabilities {
  addComment = 'add_comment',
  aiAccess = 'ai_access',
  changeHierarchy = 'change_hierarchy',
  changePermissions = 'change_permissions',
  connect = 'connect',
  deleteItem = 'delete',
  editPublishedConnection = 'edit_published_connection',
  execute = 'execute',
  explainData = 'explain_data',
  exportData = 'export_data',
  exportImage = 'export_image',
  exportXml = 'export_xml',
  filter = 'filter',
  inheritedProjectLeader = 'inherited_project_leader', // This is a read-only capability, not shown in the permissions data grid
  projectLeader = 'project_leader',
  read = 'read',
  shareView = 'share_view',
  viewComments = 'view_comments',
  vizqlDataApiAccess = 'vizql_data_api_access',
  vud = 'vud',
  webAuthoring = 'web_authoring',
  extractRefresh = 'allow_extract_refresh',
  webAuthoringForFlows = 'web_authoring_for_flows',
  write = 'write',
  xmlSaveAs = 'xml_save_as',
  pulseMetricDefine = 'pulse_metric_define',
  pulseMetricCertify = 'pulse_metric_certify',
}

export interface ICapabilityInfo {
  getIcon: () => string;
  getDisabledIcon: () => string;
  message: () => string;
  name: string;
}

export const CapabilityInfo: _.Dictionary<ICapabilityInfo> = {
  addComment: {
    message: Messages.capability_add_comment,
    getIcon: () => imgPath('permission_capability_add_comment.svg'),
    getDisabledIcon: () => imgPath('permission_capability_add_comment_disabled.svg'),
    name: Capabilities.addComment
  },
  aiAccess: {
    message: Messages.capability_ai_access,
    // Rendered via AiAccessIcon in PermissionsGridCells; SVGs below are fallbacks only
    getIcon: () => imgPath('permission_capability_explain_data.svg'),
    getDisabledIcon: () => imgPath('permission_capability_explain_data_disabled.svg'),
    name: Capabilities.aiAccess
  },
  changeHierarchy: {
    message: Messages.capability_change_hierarchy,
    getIcon: () => imgPath('permission_capability_change_hierarchy.svg'),
    getDisabledIcon: () => imgPath('permission_capability_change_hierarchy_disabled.svg'),
    name: Capabilities.changeHierarchy
  },
  changePermissions: {
    message: Messages.capability_change_permissions,
    getIcon: () => imgPath('permission_capability_change_permissions.svg'),
    getDisabledIcon: () => imgPath('permission_capability_change_permissions_disabled.svg'),
    name: Capabilities.changePermissions
  },
  connect: {
    message: Messages.capability_connect,
    getIcon: () => imgPath('permission_capability_connect.svg'),
    getDisabledIcon: () => imgPath('permission_capability_connect_disabled.svg'),
    name: Capabilities.connect
  },
  connectWorkbook: {
    message: Messages.capability_full_data_query,
    // Rendered via DataQueryIcon in PermissionsGridCells; SVGs below are fallbacks only
    getIcon: () => imgPath('permission_capability_connect.svg'),
    getDisabledIcon: () => imgPath('permission_capability_connect_disabled.svg'),
    name: Capabilities.connect
  },
  delete: {
    message: Messages.capability_delete,
    getIcon: () => imgPath('permission_capability_delete.svg'),
    getDisabledIcon: () => imgPath('permission_capability_delete_disabled.svg'),
    name: Capabilities.deleteItem
  },
  editPublishedConnection: {
    message: Messages.capability_write_content,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.editPublishedConnection
  },
  executeFlow: {
    message: Messages.capability_execute_flow,
    getIcon: () => imgPath('permission_capability_execute.svg'),
    getDisabledIcon: () => imgPath('permission_capability_execute_disabled.svg'),
    name: Capabilities.execute
  },
  explainData: {
    message: Messages.capability_explain_data,
    getIcon: () => imgPath('permission_capability_explain_data.svg'),
    getDisabledIcon: () => imgPath('permission_capability_explain_data_disabled.svg'),
    name: Capabilities.explainData
  },
  exportData: {
    message: Messages.capability_export_data,
    getIcon: () => imgPath('permission_capability_export_data.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_data_disabled.svg'),
    name: Capabilities.exportData
  },
  exportImage: {
    message: Messages.capability_export_image,
    getIcon: () => imgPath('permission_capability_export_image.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_image_disabled.svg'),
    name: Capabilities.exportImage
  },
  exportXmlDatasource: {
    message: Messages.capability_export_xml_datasource,
    getIcon: () => imgPath('permission_capability_export_xml_datasource.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_xml_datasource_disabled.svg'),
    name: Capabilities.exportXml
  },
  exportXmlFlow: {
    message: Messages.capability_export_xml_flow,
    getIcon: () => imgPath('permission_capability_export_xml_flow.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_xml_flow_disabled.svg'),
    name: Capabilities.exportXml
  },
  exportXmlWorkbook: {
    message: Messages.capability_export_xml_workbook_save_copy,
    getIcon: () => imgPath('permission_capability_export_xml_workbook.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_xml_workbook_disabled.svg'),
    name: Capabilities.exportXml
  },
  extractRefresh: {
    message: Messages.capability_allow_extract_refresh,
    getIcon: () => imgPath('permission_capability_extract_refresh.svg'),
    getDisabledIcon: () => imgPath('permission_capability_extract_refresh_disabled.svg'),
    name: Capabilities.extractRefresh
  },
  filter: {
    message: Messages.capability_filter,
    getIcon: () => imgPath('permission_capability_filter.svg'),
    getDisabledIcon: () => imgPath('permission_capability_filter_disabled.svg'),
    name: Capabilities.filter
  },
  projectLeader: {
    message: Messages.capability_project_leader,
    getIcon: () => imgPath('permission_capability_project_leader.svg'),
    getDisabledIcon: () => imgPath('permission_capability_project_leader_disabled.svg'),
    name: Capabilities.projectLeader
  },
  read: {
    message: Messages.capability_read,
    getIcon: () => imgPath('permission_capability_read.svg'),
    getDisabledIcon: () => imgPath('permission_capability_read_disabled.svg'),
    name: Capabilities.read
  },
  shareView: {
    message: Messages.capability_share_view,
    getIcon: () => imgPath('permission_capability_share_view.svg'),
    getDisabledIcon: () => imgPath('permission_capability_share_view_disabled.svg'),
    name: Capabilities.shareView
  },
  viewComments: {
    message: Messages.capability_view_comments,
    getIcon: () => imgPath('permission_capability_view_comments.svg'),
    getDisabledIcon: () => imgPath('permission_capability_view_comments_disabled.svg'),
    name: Capabilities.viewComments
  },
  vizqlDataApiAccess: {
    message: Messages.capability_vizql_data_api_access,
    getIcon: () => imgPath('permission_capability_vizql_data_api_access.svg'),
    getDisabledIcon: () => imgPath('permission_capability_vizql_data_api_access_disabled.svg'),
    name: Capabilities.vizqlDataApiAccess
  },
  vud: {
    message: Messages.capability_vud,
    getIcon: () => imgPath('permission_capability_vud.svg'),
    getDisabledIcon: () => imgPath('permission_capability_vud_disabled.svg'),
    name: Capabilities.vud
  },
  webAuthoring: {
    message: Messages.capability_web_authoring,
    getIcon: () => imgPath('permission_capability_web_authoring.svg'),
    getDisabledIcon: () => imgPath('permission_capability_web_authoring_disabled.svg'),
    name: Capabilities.webAuthoring
  },
  webAuthoringForFlows: {
    message: Messages.capability_web_authoring_for_flows,
    getIcon: () => imgPath('permission_capability_web_authoring_for_flows.svg'),
    getDisabledIcon: () => imgPath('permission_capability_web_authoring_for_flows_disabled.svg'),
    name: Capabilities.webAuthoringForFlows
  },
  write: {
    message: Messages.capability_write_content,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.write
  },
  writeProject: {
    message: Messages.capability_write_project,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.write
  },
  saveAsDatasource: {
    message: Messages.capability_xml_save_as,
    getIcon: () => imgPath('permission_capability_save_as_datasource.svg'),
    getDisabledIcon: () => imgPath('permission_capability_save_as_datasource_disabled.svg'),
    name: Capabilities.xmlSaveAs
  },
  definePulseMetric: {
    message: Messages.capability_pulse_metric_define,
    getIcon: () => imgPath('permission_capability_define_pulse_metric.svg'),
    getDisabledIcon: () => imgPath('permission_capability_define_pulse_metric_disabled.svg'),
    name: Capabilities.pulseMetricDefine
  },
  certifyPulseMetric: {
    message: Messages.capability_pulse_metric_certify,
    // Rendered via CertifiedBaseStrokeIcon in PermissionsGridCells; SVGs below are fallbacks only
    getIcon: () => imgPath('Certified_icon_column_header.svg'),
    getDisabledIcon: () => imgPath('Certified_icon_column_header.svg'),
    name: Capabilities.pulseMetricCertify
  },
};

const CollectionCapabilities = [
  Capabilities.read
];

const CatalogCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.read,
  Capabilities.write
];

const DatasourceCapabilities = (isOnlineEnvironment?: boolean, allowNonAdminRefreshExtracts?: boolean) => [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.write,
  Capabilities.xmlSaveAs,
  ...isOnlineEnvironment ? [Capabilities.pulseMetricDefine] : [],
  ...isOnlineEnvironment && FeatureFlags.ntbue_CHECK_METRIC_CERTIFY_CAPEnabled ? [Capabilities.pulseMetricCertify] : [],
  ...allowNonAdminRefreshExtracts ? [Capabilities.extractRefresh] : [],
  Capabilities.vizqlDataApiAccess
];

const FlowCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.execute,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.webAuthoringForFlows,
  Capabilities.write
];

const PublishedConnectionCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.editPublishedConnection,
  Capabilities.read,
];

const ProjectCapabilities = [
  Capabilities.read,
  Capabilities.write
];

const ViewCapabilities = (
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
) => [
  Capabilities.addComment,
  ...analyticsAssistantDashboardQAEnabled &&
  einsteinCopilotForTableauEnabled
    ? [Capabilities.aiAccess]
    : [],
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  Capabilities.webAuthoring
];

const WorkbookCapabilities = (
  allowNonAdminRefreshExtracts?: boolean,
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
) => [
  Capabilities.addComment,
  ...analyticsAssistantDashboardQAEnabled &&
  einsteinCopilotForTableauEnabled
    ? [Capabilities.aiAccess]
    : [],
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.explainData,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.exportXml,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  ...allowNonAdminRefreshExtracts ? [Capabilities.extractRefresh] : [],
  Capabilities.webAuthoring,
  Capabilities.write,
  Capabilities.vizqlDataApiAccess
];

export const CapabilitiesByAuthorizableType = (
  isOnlineEnvironment?: boolean,
  allowNonAdminRefreshExtracts?: boolean,
  analyticsAssistantDashboardQAEnabled?: boolean,
  einsteinCopilotForTableauEnabled?: boolean,
): _.Dictionary<Capabilities[]> => {
  return {
    [AuthorizableType.COLLECTION]: CollectionCapabilities,
    [AuthorizableType.DATABASE]: CatalogCapabilities,
    [AuthorizableType.DATABASE_TABLE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.DATASOURCE]: DatasourceCapabilities(isOnlineEnvironment, allowNonAdminRefreshExtracts),
    [AuthorizableType.FLOW]: FlowCapabilities,
    [AuthorizableType.PUBLISHED_CONNECTION]: PublishedConnectionCapabilities,
    [AuthorizableType.PROJECT]: ProjectCapabilities,
    [AuthorizableType.PROJECT_DATABASE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.PROJECT_DATASOURCE_TEMPLATE]: DatasourceCapabilities(isOnlineEnvironment, allowNonAdminRefreshExtracts),
    [AuthorizableType.PROJECT_FLOW_TEMPLATE]: FlowCapabilities,
    [AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE]: PublishedConnectionCapabilities,
    [AuthorizableType.PROJECT_TABLE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.PROJECT_WORKBOOK_TEMPLATE]: WorkbookCapabilities(
      allowNonAdminRefreshExtracts,
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
    [AuthorizableType.TABLE]: CatalogCapabilities,
    [AuthorizableType.VIEW]: ViewCapabilities(
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
    [AuthorizableType.WORKBOOK]: WorkbookCapabilities(
      allowNonAdminRefreshExtracts,
      analyticsAssistantDashboardQAEnabled,
      einsteinCopilotForTableauEnabled,
    ),
  };
};
